import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useQueryUrl } from "../../../../hooks/QueryUrl";
import { api, SubscriptionDetail } from "../../../../services/api";
import { isApiError } from "../../../../services/api/errors";

/** The subscription of the Keepo in `?site=`, for the screens that manage it (none: back to the Keepos). */
export const useManagedSubscription = () => {
  const navigate = useNavigate();
  const queryUrl = useQueryUrl();
  const siteId = queryUrl.get("site") || "";

  const [isLoading, setLoading] = useState(true);
  const [detail, setDetail] = useState<SubscriptionDetail>();
  const [siteTitle, setSiteTitle] = useState<string>();

  useEffect(() => {
    if (!siteId) {
      navigate("/sites", { replace: true });
      return;
    }
    Promise.all([api.getSubscriptionDetail(siteId).catch(() => undefined), api.getSites().catch(() => [])]).then(
      ([found, sites]) => {
        if (!found || !Object.keys(found).length) {
          navigate("/sites", { replace: true });
          return;
        }
        setDetail(found);
        const site = sites.find(item => item.siteId === siteId);
        setSiteTitle((site?.site || site?.draft)?.title);
        setLoading(false);
      }
    );
  }, [navigate, siteId]);

  const lastCard = [...(detail?.invoices || [])]
    .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
    .find(invoice => invoice.card)?.card;

  return {
    siteId,
    siteTitle,
    detail,
    lastCard,
    isLoading,
    isAnnual: detail?.plan?.interval === "year",
    detailUrl: `/subscription/detail?site=${encodeURIComponent(siteId)}`,
  };
};

/**
 * Saving on the manage screens: runs the change on the API, shows the "done" note, then goes back to the
 * subscription. On failure the screen shows why (`error`: "cardDeclined" or "generic").
 */
export const useSave = (detailUrl: string, navigate: (to: string) => void) => {
  const [state, setState] = useState<"idle" | "saving" | "done">("idle");
  const [error, setError] = useState<"cardDeclined" | "generic">();

  const save = async (change: () => Promise<unknown>) => {
    setState("saving");
    setError(undefined);
    try {
      await change();
      // The plan/payment changed: the next reads must come from the server
      api.clearCacheSites();
      setState("done");
      window.setTimeout(() => navigate(detailUrl), 1500);
    } catch (e) {
      setError(isApiError(e, "CARD_DECLINED") ? "cardDeclined" : "generic");
      setState("idle");
    }
  };

  return { state, error, save };
};
